import {
  fetchFrameworkMasterApi,
  fetchTrustCenterFrameworkTogglesApi,
  updateTrustCenterFrameworkToggleApi,
} from "../services/axios/AuthApis/TrustCenterFrameworkApis";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  MapPinIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  PuzzlePieceIcon,
  CpuChipIcon,
} from "@heroicons/react/24/outline";
import {
  fetchCustomerRequirementsApi,
  updateAIAssetManagementToggleApi,
} from "../services/axios/AuthApis/crmApis";
import {
  getConnectorsByCategoryApi,
  getConnectorsCategoryApi,
} from "../services/axios/AuthApis/AssetManagementApis";
import { framework_categories } from "../constants/AppConstants";
import { useSnackbar } from "../context/SnackbarContext";
import ApiLoader from "./ui/ApiLoader";

const TABS = [
  { type: "Default", label: "Default", icon: Squares2X2Icon },
  { type: "TrustCenter", label: "Trust Center", icon: ShieldCheckIcon },
  { type: "AdditionalModule", label: "Additional Modules", icon: PuzzlePieceIcon },
  { type: "AIAssetManagement", label: "AI Asset Management", icon: CpuChipIcon },
];

// Show a search box in a framework category once it has more than this many.
const SEARCH_THRESHOLD = 6;

const formatRole = (role) =>
  (role || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("") || "?";

const Switch = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${
      checked ? "bg-primary-700" : "bg-gray-300 dark:bg-gray-600"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
        checked ? "translate-x-[18px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

const ToggleRow = ({ title, checked, pending, onToggle, icon: Icon = ShieldCheckIcon, compact = false }) => (
  <div
    className={`flex items-center justify-between gap-3 rounded-lg border transition-colors ${
      compact ? "px-2.5 py-1.5" : "px-3 py-2"
    } ${
      checked
        ? "border-primary-200 bg-primary-50/60 dark:border-primary-700 dark:bg-primary-900/20"
        : "border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900"
    }`}
  >
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${
          checked
            ? "bg-primary-100 text-primary-700 dark:bg-primary-800 dark:text-primary-100"
            : "bg-gray-100 text-gray-400 dark:bg-gray-800"
        }`}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{title}</p>
        <p className={`text-[11px] leading-4 ${checked ? "text-primary-700 dark:text-primary-200" : "text-gray-500 dark:text-gray-400"}`}>
          {pending ? "Saving…" : checked ? "Enabled" : "Not enabled"}
        </p>
      </div>
    </div>
    <Switch
      checked={checked}
      disabled={pending}
      onChange={onToggle}
      label={`${checked ? "Disable" : "Enable"} ${title}`}
    />
  </div>
);

const EmptyState = ({ icon: Icon = ShieldCheckIcon, text }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <Icon className="mb-2 h-8 w-8 text-gray-300" />
    <p className="text-sm text-gray-500 dark:text-gray-400">{text}</p>
  </div>
);

const CustomerDetailsModal = ({
  show,
  onClose,
  selectedCustomer,
  customerActiveTab,
  handleTabClick,
  categories,
  selectedCategory,
  handleCategoryClick,
  subSubCategories,
  subCategoryToggles,
  handleSubCategoryToggle,
  modules,
  additionalModules,
  handleAdditionalToggle,
  handleVaptAccessToggle,
  setAdditionalModules,
  AIAssestManagerModules,
}) => {
  const { showSnackbar } = useSnackbar();

  // Frameworks state for Trust Center
  const [trustFrameworks, setTrustFrameworks] = useState([]);
  const [trustCenterLoadingLocal, setTrustCenterLoadingLocal] = useState(false);
  const [frameworkToggles, setFrameworkToggles] = useState({});
  const [selectedFrameworkCategory, setSelectedFrameworkCategory] = useState(null);
  const [frameworkSearch, setFrameworkSearch] = useState("");
  const [connectorCategories, setConnectorCategories] = useState([]);
  const [selectedConnectorCategory, setSelectedConnectorCategory] = useState("");
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connectorToggle, setConnectorToggle] = useState({});
  // Switches waiting for the server, keyed "fw:<id>", "mod:<name>", "vapt:<key>", "conn:<id>".
  const [pending, setPending] = useState({});

  const specialModuleKeys = ["AI LMS", "AI Asset Manager"];
  // "AI VAPT Access" holds the {asset_vapt, external_vapt} sub-toggle
  // object, not a plain boolean — render it separately (nested under the
  // "AI VAPT" row below) rather than in the generic boolean toggle list.
  const nestedModuleKeys = ["AI VAPT Access"];
  const visibleAdditionalModules = Object.fromEntries(
    Object.entries(additionalModules || {}).filter(
      ([module]) => !specialModuleKeys.includes(module) && !nestedModuleKeys.includes(module),
    ),
  );
  const vaptAccess = additionalModules?.["AI VAPT Access"] || {
    asset_vapt: false,
    external_vapt: false,
  };

  const customerName =
    [selectedCustomer?.firstname, selectedCustomer?.lastname].filter(Boolean).join(" ") ||
    selectedCustomer?.sender ||
    "Unnamed customer";
  const companyName = selectedCustomer?.company || customerName;
  const activeTab = customerActiveTab || "Default";
  const isActive = (selectedCustomer?.status || "").toLowerCase() === "active";

  // The parent resets the tab to "" for each customer; open on Default.
  useEffect(() => {
    if (show && selectedCustomer && !customerActiveTab) handleTabClick("Default");
  }, [show, selectedCustomer, customerActiveTab]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  useEffect(() => {
    if (customerActiveTab === "TrustCenter" && show) {
      setTrustCenterLoadingLocal(true);
      const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;
      Promise.all([
        fetchFrameworkMasterApi(),
        customerId ? fetchTrustCenterFrameworkTogglesApi(customerId) : Promise.resolve({}),
      ])
        .then(([frameworks, toggles]) => {
          setTrustFrameworks(frameworks);
          setFrameworkToggles(toggles);
        })
        .catch(() => {
          setTrustFrameworks([]);
          setFrameworkToggles({});
        })
        .finally(() => {
          setSelectedFrameworkCategory(null);
          setFrameworkSearch("");
          setTrustCenterLoadingLocal(false);
        });
    }
  }, [customerActiveTab, show, selectedCustomer]);

  useEffect(() => {
    if (!show) return;

    const loadModules = async () => {
      try {
        const addOnModules = await fetchCustomerRequirementsApi(selectedCustomer?.id, "AdditionalModule");
        const filteredAddOnModules = Object.fromEntries(
          Object.entries(addOnModules || {}).filter(([module]) => !specialModuleKeys.includes(module)),
        );
        // Update additionalModules state without module-only entries
        setAdditionalModules(filteredAddOnModules);
      } catch (err) {
        console.error(err);
      }
    };

    loadModules();
  }, [show]);

  const loadConnectorCategories = async () => {
    try {
      const result = await getConnectorsCategoryApi();
      setConnectorCategories(result.results);
    } catch (error) {
      console.error("Failed to load connectors:", error);
      setConnectorCategories([]);
    }
  };

  useEffect(() => {
    loadConnectorCategories();
  }, []);

  // Runs one switch change: marks it saving, then tells the user how it went.
  // `action` resolves to false when the change was not saved.
  const runToggle = async (key, label, enabled, action) => {
    setPending((p) => ({ ...p, [key]: true }));
    try {
      const saved = await action();
      if (saved === false) {
        showSnackbar(`Couldn't update ${label}. Please try again.`, "error");
      } else {
        showSnackbar(
          `${label} ${enabled ? "enabled" : "disabled"} for ${companyName}`,
          enabled ? "success" : "info",
        );
      }
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
  };

  const handleFrameworkToggle = (framework) => {
    const customerId = selectedCustomer?.id || selectedCustomer?.customer_id;
    if (!customerId) return;
    const prev = !!frameworkToggles[framework._id];
    const updated = !prev;
    setFrameworkToggles((p) => ({ ...p, [framework._id]: updated }));
    runToggle(`fw:${framework._id}`, framework.name, updated, async () => {
      try {
        await updateTrustCenterFrameworkToggleApi(customerId, framework._id, updated);
        return true;
      } catch {
        setFrameworkToggles((p) => ({ ...p, [framework._id]: prev }));
        return false;
      }
    });
  };

  const handleModuleToggle = (module, enabled) =>
    runToggle(`mod:${module}`, module, !enabled, () => handleAdditionalToggle(module));

  const handleVaptToggle = (key, label) =>
    runToggle(`vapt:${key}`, label, !vaptAccess[key], () => handleVaptAccessToggle(key));

  const handleConnectorCategoryClick = async (categoryId) => {
    setSelectedConnectorCategory(categoryId);
    setLoading(true);
    setConnectors([]);

    try {
      const result = await getConnectorsByCategoryApi(categoryId);
      const activeConnectors = result.connectors.filter((c) => c.Active === true);
      setConnectors(activeConnectors || []);
      setConnectorToggle((prev) => ({ ...prev, ...AIAssestManagerModules }));
    } catch (err) {
      console.error("Failed to fetch connectors", err);
      setConnectors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectorToggle = (connector) => {
    const prev = !!connectorToggle[connector.id];
    const updated = !prev;
    setConnectorToggle((p) => ({ ...p, [connector.id]: updated }));
    runToggle(`conn:${connector.id}`, connector.name, updated, async () => {
      try {
        await updateAIAssetManagementToggleApi(selectedCustomer?.id, connector.id, updated);
        return true;
      } catch (err) {
        console.error("Failed to update connector toggle:", err);
        setConnectorToggle((p) => ({ ...p, [connector.id]: prev }));
        return false;
      }
    });
  };

  if (!show || !selectedCustomer) return null;

  const frameworksIn = (category) => trustFrameworks.filter((fw) => fw.category === category);
  const enabledCount = (frameworks) => frameworks.filter((fw) => frameworkToggles[fw._id]).length;
  const categoryFrameworks = selectedFrameworkCategory ? frameworksIn(selectedFrameworkCategory) : [];
  const visibleFrameworks = categoryFrameworks.filter((fw) =>
    fw.name.toLowerCase().includes(frameworkSearch.trim().toLowerCase()),
  );
  const contactItems = [
    selectedCustomer.email && { icon: EnvelopeIcon, text: selectedCustomer.email, href: `mailto:${selectedCustomer.email}` },
    selectedCustomer.phone && { icon: PhoneIcon, text: selectedCustomer.phone, href: `tel:${selectedCustomer.phone}` },
    selectedCustomer.location && { icon: MapPinIcon, text: selectedCustomer.location },
  ].filter(Boolean);

  const renderTrustCenter = () => {
    if (trustCenterLoadingLocal) return <ApiLoader label="Loading frameworks..." />;

    if (!selectedFrameworkCategory) {
      const totalEnabled = enabledCount(trustFrameworks);
      return (
        <div className="space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-900 dark:text-white">{totalEnabled}</span> of{" "}
            {trustFrameworks.length} frameworks enabled. Choose a category to change them.
          </p>
          {framework_categories.map((category) => {
            const frameworks = frameworksIn(category.value);
            const enabled = enabledCount(frameworks);
            return (
              <button
                key={category.value}
                type="button"
                onClick={() => {
                  setSelectedFrameworkCategory(category.value);
                  setFrameworkSearch("");
                }}
                className="group flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left transition-colors hover:border-primary-300 hover:bg-primary-50/50 dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
                    <ShieldCheckIcon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{category.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {frameworks.length} framework{frameworks.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {enabled > 0 && (
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-800 dark:text-primary-100">
                      {enabled} enabled
                    </span>
                  )}
                  <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-primary-600" />
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    const categoryName = framework_categories.find((c) => c.value === selectedFrameworkCategory)?.name;
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setSelectedFrameworkCategory(null)}
            className="flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-900 dark:text-primary-200"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            All categories
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {enabledCount(categoryFrameworks)} of {categoryFrameworks.length} enabled
          </span>
        </div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{categoryName}</h3>
        {categoryFrameworks.length > SEARCH_THRESHOLD && (
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={frameworkSearch}
              onChange={(e) => setFrameworkSearch(e.target.value)}
              placeholder="Search frameworks"
              className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            />
          </div>
        )}
        {visibleFrameworks.length > 0 ? (
          <div className="space-y-1.5">
            {visibleFrameworks.map((fw) => (
              <ToggleRow
                key={fw._id}
                title={fw.name}
                checked={!!frameworkToggles[fw._id]}
                pending={!!pending[`fw:${fw._id}`]}
                onToggle={() => handleFrameworkToggle(fw)}
              />
            ))}
          </div>
        ) : (
          <EmptyState text={frameworkSearch ? "No frameworks match your search" : "No frameworks in this category"} />
        )}
      </div>
    );
  };

  const renderDefault = () =>
    Object.keys(modules || {}).length > 0 ? (
      <div className="space-y-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">Included for every customer.</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {Object.keys(modules).map((module) => (
            <div
              key={module}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
            >
              <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{module}</span>
            </div>
          ))}
        </div>
      </div>
    ) : (
      <EmptyState icon={Squares2X2Icon} text="No default modules" />
    );

  const renderAdditionalModules = () => {
    const entries = Object.entries(visibleAdditionalModules);
    if (entries.length === 0) return <EmptyState icon={PuzzlePieceIcon} text="No additional modules" />;
    return (
      <div className="space-y-2">
        {entries.map(([module, enabled]) => (
          <React.Fragment key={module}>
            <ToggleRow
              title={module}
              icon={PuzzlePieceIcon}
              checked={!!enabled}
              pending={!!pending[`mod:${module}`]}
              onToggle={() => handleModuleToggle(module, !!enabled)}
            />
            {module === "AI VAPT" && enabled && (
              <div className="ml-5 space-y-2 border-l-2 border-primary-100 pl-4 dark:border-primary-800">
                <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">VAPT access</p>
                {[
                  { key: "asset_vapt", label: "Asset VAPT" },
                  { key: "external_vapt", label: "External VAPT" },
                ].map(({ key, label }) => (
                  <ToggleRow
                    key={key}
                    compact
                    title={label}
                    checked={!!vaptAccess[key]}
                    pending={!!pending[`vapt:${key}`]}
                    onToggle={() => handleVaptToggle(key, label)}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderAssetManagement = () => {
    if (!Array.isArray(connectorCategories) || connectorCategories.length === 0) {
      return <EmptyState icon={CpuChipIcon} text="No connector categories available" />;
    }
    return (
      <div className="space-y-2">
        {connectorCategories.map((cat) => {
          const categoryId = cat.id?.$oid || cat.id;
          const isExpanded = selectedConnectorCategory === categoryId;
          return (
            <div
              key={categoryId}
              className={`overflow-hidden rounded-lg border ${
                isExpanded ? "border-primary-200 dark:border-primary-700" : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => {
                  if (isExpanded) {
                    setSelectedConnectorCategory(null);
                    setConnectors([]);
                  } else {
                    handleConnectorCategoryClick(categoryId);
                  }
                }}
                className={`flex w-full items-center justify-between px-3 py-2 text-left transition-colors ${
                  isExpanded ? "bg-primary-50 dark:bg-primary-900/30" : "bg-white hover:bg-gray-50 dark:bg-gray-900"
                }`}
              >
                <span className={`text-sm font-medium ${isExpanded ? "text-primary-800 dark:text-primary-100" : "text-gray-800 dark:text-gray-100"}`}>
                  {cat.name}
                </span>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180 text-primary-700" : "text-gray-400"}`}
                />
              </button>
              {isExpanded && (
                <div className="space-y-1.5 border-t border-primary-100 bg-white p-2 dark:border-primary-800 dark:bg-gray-900">
                  {loading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-700" />
                    </div>
                  ) : connectors.length > 0 ? (
                    connectors.map((connector) => (
                      <ToggleRow
                        key={connector.id}
                        compact
                        icon={CpuChipIcon}
                        title={connector.name}
                        checked={!!connectorToggle[connector.id]}
                        pending={!!pending[`conn:${connector.id}`]}
                        onToggle={() => handleConnectorToggle(connector)}
                      />
                    ))
                  ) : (
                    <p className="py-4 text-center text-sm italic text-gray-500 dark:text-gray-400">No connectors found</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.2 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-details-title"
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900"
      >
        {/* Header */}
        <div className="border-b border-gray-100 px-5 pb-3 pt-4 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-800 text-sm font-semibold text-white">
                {initialsOf(customerName)}
              </div>
              <div className="min-w-0">
                <h2 id="customer-details-title" className="truncate text-base font-semibold leading-6 text-gray-900 dark:text-white">
                  {customerName}
                </h2>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  {selectedCustomer.role && (
                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
                      {formatRole(selectedCustomer.role)}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      isActive
                        ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-green-500" : "bg-gray-400"}`} />
                    {selectedCustomer.status ? formatRole(selectedCustomer.status) : "Unknown"}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
            <span className="inline-flex items-center gap-1.5">
              <BuildingOfficeIcon className="h-3.5 w-3.5 text-gray-400" />
              {selectedCustomer.company || "No company assigned"}
            </span>
            {contactItems.map(({ icon: Icon, text, href }) => (
              <span key={text} className="inline-flex min-w-0 items-center gap-1.5">
                <Icon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                {href ? (
                  <a href={href} className="truncate hover:text-primary-700 hover:underline">
                    {text}
                  </a>
                ) : (
                  <span className="truncate">{text}</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-100 px-5 py-2 dark:border-gray-800">
          <div role="tablist" className="flex gap-0.5 overflow-x-auto rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
            {TABS.map(({ type, label, icon: Icon }) => {
              const selected = activeTab === type;
              return (
                <button
                  key={type}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => handleTabClick(type)}
                  className={`flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                    selected
                      ? "bg-white text-primary-800 shadow-sm dark:bg-gray-900 dark:text-white"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content: the only part that scrolls */}
        <div className="min-h-[200px] flex-1 overflow-y-auto px-5 py-3">
          {activeTab === "Default" && renderDefault()}
          {activeTab === "TrustCenter" && renderTrustCenter()}
          {activeTab === "AdditionalModule" && renderAdditionalModules()}
          {activeTab === "AIAssetManagement" && renderAssetManagement()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-5 py-2 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">Changes are saved as soon as you switch them.</p>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CustomerDetailsModal;
